// src/products.js
//
// The storefront's side of the catalogue: which products a visitor sees, how
// many of one they can order, and the cart they are collecting them in.
//
// The products themselves come from Firestore (db.getStorefrontProducts);
// nothing about any one product is written here.

import { LEGACY_PRODUCTS } from './catalogue';

/**
 * Whether the storefront shows a product at all: not retired, and either for
 * sale or switched on as a showcase.
 */
export const visibleOnStorefront = (product) =>
  Boolean(product) &&
  product.archived !== true &&
  (product.isForSale !== false || product.showcase === true);

/**
 * How many units of a product a customer can actually order: none while it is
 * off sale or retired, and never fewer than none. `available` can sit below
 * zero — an admin order may reserve more than was on hand — and a negative
 * figure must read as out of stock rather than leak into quantities and
 * comparisons.
 */
export const sellableStock = (row) =>
  row && row.isForSale !== false && row.archived !== true
    ? Math.max(0, Number(row.available) || 0)
    : 0;

//
// ——— THE CART ————————————————————————————————————————————————————————————
//
// One localStorage entry, `cart`:
//
//   { items:  { productId: quantity },
//     prices: { productId: unit price },
//     names:  { productId: name } }
//
// `prices` and `names` are what the cart was last shown with, so it can be
// drawn at once — and still drawn if the catalogue cannot be read — before the
// live figures arrive. Checkout never trusts them: the server prices every
// order from the catalogue.

const CART_KEY = 'cart';

const wholeCounts = (value) => {
  const out = {};
  Object.entries(value && typeof value === 'object' ? value : {}).forEach(([id, qty]) => {
    const n = Math.floor(Number(qty));
    if (Number.isFinite(n) && n > 0) out[String(id)] = n;
  });
  return out;
};

const amounts = (value) => {
  const out = {};
  Object.entries(value && typeof value === 'object' ? value : {}).forEach(([id, price]) => {
    const n = Number(price);
    if (Number.isFinite(n) && n >= 0) out[String(id)] = n;
  });
  return out;
};

const labels = (value) => {
  const out = {};
  Object.entries(value && typeof value === 'object' ? value : {}).forEach(([id, name]) => {
    if (typeof name === 'string' && name.trim() !== '') out[String(id)] = name;
  });
  return out;
};

/**
 * Reads the cart, tolerating a missing or corrupted one. A cart saved before
 * products could be added — one field per product, qtyA, priceA and so on —
 * is read as the same products under their ids, so nobody's basket empties
 * because the shop changed underneath it.
 */
export const readStoredCart = () => {
  let stored;
  try {
    stored = JSON.parse(localStorage.getItem(CART_KEY) || '{}') || {};
  } catch (err) {
    stored = {};
  }
  if (stored.items && typeof stored.items === 'object') {
    return {
      items: wholeCounts(stored.items),
      prices: amounts(stored.prices),
      names: labels(stored.names),
    };
  }
  const cart = { items: {}, prices: {}, names: {} };
  LEGACY_PRODUCTS.forEach((product) => {
    const qty = Math.floor(Number(stored[product.qtyField]));
    if (!Number.isFinite(qty) || qty <= 0) return;
    cart.items[product.id] = qty;
    cart.names[product.id] = product.name;
    const price = Number(stored[product.priceField]);
    if (Number.isFinite(price) && price > 0) cart.prices[product.id] = price;
  });
  return cart;
};

/**
 * Writes quantities (by product id) and the prices and names they were shown
 * with, then tells the rest of the app. The header badge and the dropdown both
 * listen for `cartUpdated`, so skipping the event is what makes a cart edit
 * look like it did nothing. Products at zero are dropped.
 */
export const writeStoredCart = (items, shownPrices = {}, shownNames = {}) => {
  const kept = wholeCounts(items);
  const knownPrices = amounts(shownPrices);
  const knownNames = labels(shownNames);
  const prices = {};
  const names = {};
  Object.keys(kept).forEach((id) => {
    if (id in knownPrices) prices[id] = knownPrices[id];
    if (id in knownNames) names[id] = knownNames[id];
  });
  try {
    localStorage.setItem(CART_KEY, JSON.stringify({ items: kept, prices, names }));
  } catch (err) {
    // A full or blocked store leaves the cart as it was.
  }
  window.dispatchEvent(new Event('cartUpdated'));
};

/** How many units are in the cart, across every product. */
export const cartCount = (cart = readStoredCart()) =>
  Object.values(cart.items || {}).reduce((sum, qty) => sum + qty, 0);

/**
 * Adds units of one product to the cart, never past `limit` (what can be
 * sold). Returns the quantity the cart now holds and whether it had to be
 * held back to the limit.
 */
export const addToCart = (product, quantity, limit) => {
  const cart = readStoredCart();
  const id = String(product.id);
  const wanted = (cart.items[id] || 0) + Math.max(0, Math.floor(Number(quantity) || 0));
  const cap = Math.max(0, Number(limit) || 0);
  const qty = Math.min(wanted, cap);
  writeStoredCart(
    { ...cart.items, [id]: qty },
    { ...cart.prices, [id]: Number(product.price) || 0 },
    { ...cart.names, [id]: product.name || cart.names[id] || '' }
  );
  return { qty, trimmed: wanted > cap };
};

/**
 * A stored cart checked against the live catalogue: each quantity cut to what
 * can be sold (a product off sale, retired or gone drops out), and the prices
 * and names brought up to date.
 *
 * Returns `{ items, prices, names, stock, trimmed }`, where `stock` is what can
 * be sold of every product in the catalogue, and `trimmed` lists the ids of the
 * products still on sale whose quantity had to come down to what is in stock.
 */
export function reconcileCart(cart, products) {
  const byId = new Map((products || []).map((product) => [String(product.id), product]));
  const result = { items: {}, prices: {}, names: {}, stock: {}, trimmed: [] };
  (products || []).forEach((product) => {
    result.stock[String(product.id)] = sellableStock(product);
  });
  Object.entries(cart.items || {}).forEach(([id, qty]) => {
    const product = byId.get(id);
    const limit = sellableStock(product);
    const kept = Math.min(qty, limit);
    if (product) {
      result.prices[id] = Number(product.price) || 0;
      result.names[id] = product.name || cart.names?.[id] || '';
      if (kept < qty && product.isForSale !== false && product.archived !== true) {
        result.trimmed.push(id);
      }
    }
    if (kept > 0) result.items[id] = kept;
  });
  return result;
}

/**
 * The ids in a cart in the shop's own order — the order of `products`, which
 * comes sorted from the catalogue — with anything not in it kept at the end.
 */
export function cartOrder(items, products) {
  const position = new Map((products || []).map((product, index) => [String(product.id), index]));
  return Object.keys(items || {}).sort(
    (a, b) => (position.get(a) ?? Number.MAX_SAFE_INTEGER) - (position.get(b) ?? Number.MAX_SAFE_INTEGER)
  );
}
